import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, NgxsOnInit } from '@ngxs/store';
import { UserApi, RoleApi, RoleDTO, UserDTO } from '@lkp/parking-sdk';
import { USER_API, ROLE_API } from '../../providers/api.module';
import * as Actions from './user.actions';
import { tap } from 'rxjs';

const DRIVER_MODE_KEY = 'driver-mode';
const DRIVER_MODE_PREFERENCE_KEY = 'driver-mode-preference';

export interface UsersStateModel {
  users: UserDTO[];
  loggedInUser?: UserDTO;
  roles: RoleDTO[];
  totalUsers: number;
  driverMode: boolean;
  driverModePreference: boolean;
}

@State<UsersStateModel>({
  name: 'users',
  defaults: {
    users: [],
    loggedInUser: undefined,
    roles: [],
    totalUsers: 0,
    driverMode: false,
    driverModePreference: false,
  },
})
@Injectable()
export class UsersState implements NgxsOnInit {
  constructor(
    @Inject(USER_API) readonly userApi: UserApi,
    @Inject(ROLE_API) readonly roleApi: RoleApi
  ) {}

  ngxsOnInit(ctx: StateContext<UsersStateModel>): void {
    try {
      const persisted = sessionStorage.getItem(DRIVER_MODE_KEY);
      const preference = localStorage.getItem(DRIVER_MODE_PREFERENCE_KEY);
      if (persisted !== null) {
        ctx.patchState({ driverMode: persisted === 'true' });
      }
      if (preference === 'true' || preference === 'false') {
        ctx.patchState({ driverModePreference: preference === 'true' });
      }
    } catch {
      // ignore storage errors
    }
  }

  @Selector()
  static loggedInUser(state: UsersStateModel) {
    return state.loggedInUser;
  }

  @Selector()
  static driverMode(state: UsersStateModel) {
    // If user not yet loaded, allow driverMode to be true tentatively (persisted flag),
    // and enforce partner precedence once roles are available.
    if (!state.loggedInUser) {
      return state.driverMode;
    }
    return (
      state.driverMode &&
      !state.loggedInUser.roles?.some((role) => role.name === 'ROLE_PARTNER')
    );
  }

  @Selector()
  static driverModePreference(state: UsersStateModel) {
    return state.driverModePreference;
  }

  @Selector()
  static currentUsers(state: UsersStateModel) {
    return state.users;
  }

  @Selector()
  static currentRoles(state: UsersStateModel) {
    return state.roles;
  }

  @Selector()
  static totalUsers(state: UsersStateModel) {
    return state.totalUsers;
  }

  private refreshUsers(context: StateContext<UsersStateModel>) {
    return context.dispatch(
      new Actions.GetUsers(0, 50, undefined, undefined, 'username', 'asc')
    );
  }

  @Action(Actions.GetUsers)
  getUsers(context: StateContext<UsersStateModel>, action: Actions.GetUsers) {
    const {
      pageIndex,
      pageSize,
      searchTerm,
      roleName,
      sortColumn,
      sortDirection,
    } = action;
    const sort = `${sortColumn},${sortDirection}`;

    return this.userApi
      .getUsers({ page: pageIndex, size: pageSize, sort, searchTerm, roleName })
      .pipe(
        tap({
          next: (resp) =>
            context.patchState({
              users: resp?.content ?? [],
              totalUsers: resp?.totalElements ?? 0,
            }),
          error: (error) => console.error('Error fetching users:', error),
        })
      );
  }

  @Action(Actions.SetDriverMode)
  setDriverMode(
    context: StateContext<UsersStateModel>,
    action: Actions.SetDriverMode
  ) {
    context.patchState({ driverMode: action.enabled });
    try {
      sessionStorage.setItem(DRIVER_MODE_KEY, String(action.enabled));
    } catch {}
  }

  @Action(Actions.SetDriverModePreference)
  setDriverModePreference(
    context: StateContext<UsersStateModel>,
    action: Actions.SetDriverModePreference
  ) {
    context.patchState({ driverModePreference: action.enabled });
    try {
      localStorage.setItem(DRIVER_MODE_PREFERENCE_KEY, String(action.enabled));
    } catch {}
  }

  @Action(Actions.ClearDriverMode)
  clearDriverMode(context: StateContext<UsersStateModel>) {
    context.patchState({ driverMode: false });
    try {
      sessionStorage.removeItem(DRIVER_MODE_KEY);
    } catch {}
  }

  @Action(Actions.SetLoggedInUser)
  setLoggedInUser(context: StateContext<UsersStateModel>) {
    return this.userApi.getMe().pipe(
      tap({
        next: (user) => context.patchState({ loggedInUser: user }),
        error: (error) =>
          console.error('Error fetching logged in user: ', error),
      })
    );
  }

  @Action(Actions.GetRoles)
  getRoles(context: StateContext<UsersStateModel>) {
    return this.roleApi.getRoles().pipe(
      tap({
        next: (roles) => context.patchState({ roles }),
        error: (error) => console.error('Error fetching roles: ', error),
      })
    );
  }

  @Action(Actions.AddUser)
  addUser(context: StateContext<UsersStateModel>, action: Actions.AddUser) {
    const { user } = action;

    return this.userApi.createUser({ userDTO: user }).pipe(
      tap({
        next: () => this.refreshUsers(context),
        error: (error) => console.error('Error creating user:', error),
      })
    );
  }

  @Action(Actions.UpdateUser)
  updateUser(
    context: StateContext<UsersStateModel>,
    action: Actions.UpdateUser
  ) {
    return this.userApi
      .updateUser({ userId: action.userId, userDTO: action.updatedUser })
      .pipe(
        tap({
          next: () => this.refreshUsers(context),
          error: (error) => console.error('Error updating user:', error),
        })
      );
  }

  @Action(Actions.RemoveUser)
  removeUser(
    context: StateContext<UsersStateModel>,
    action: Actions.RemoveUser
  ) {
    return this.userApi.deleteUser({ userId: action.userId }).pipe(
      tap({
        next: () => this.refreshUsers(context),
        error: (error) => console.error('Error deleting user:', error),
      })
    );
  }

  @Action(Actions.ResetUsersState)
  resetState(context: StateContext<UsersStateModel>) {
    const { driverModePreference } = context.getState();
    context.setState({
      users: [],
      loggedInUser: undefined,
      roles: [],
      totalUsers: 0,
      driverMode: false,
      driverModePreference,
    });
    try {
      sessionStorage.removeItem(DRIVER_MODE_KEY);
    } catch {}
  }
}
